import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { CF } from "../../theme/turquoiseTheme.js";

const DASH = {
  card: "rgba(6, 12, 28, 0.36)",
  cardElevated: "rgba(8, 16, 36, 0.4)",
  border: "rgba(94, 234, 212, 0.45)",
  text: "#FFFFFF",
  textSoft: "#F0FDFA",
  textMuted: "#D4EEEB",
  textDim: "#A8C8C4",
  textShadow: {
    textShadowColor: "rgba(0, 0, 0, 0.9)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  textShadowSoft: {
    textShadowColor: "rgba(0, 0, 0, 0.65)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  gradientStart: "#0D9488",
  gradientEnd: CF.purpleAccent,
};

/**
 * @param {{ percent: number, size?: number, stroke?: number, children?: React.ReactNode }} props
 */
export function DonutGauge({ percent, size = 100, stroke = 10, children }) {
  const p = Math.min(1, Math.max(0, percent / 100));
  const active = (n) => (p >= n ? CF.primaryBright : "rgba(45, 212, 191, 0.12)");

  return (
    <View style={{ width: size, height: size, alignItems: "center", justifyContent: "center" }}>
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: stroke,
          borderColor: "rgba(45, 212, 191, 0.12)",
        }}
      />
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: stroke,
          borderTopColor: active(0.001),
          borderRightColor: active(0.25),
          borderBottomColor: active(0.5),
          borderLeftColor: active(0.75),
          transform: [{ rotate: "-90deg" }],
        }}
      />
      {children}
    </View>
  );
}

/**
 * @param {{ data: { label: string, value: number, max?: number }[], height?: number }} props
 */
export function HorizontalBarChart({ data, height = 120 }) {
  const maxVal = Math.max(1, ...data.map((d) => d.value));

  return (
    <View style={[barStyles.wrap, { minHeight: height }]}>
      {data.map((item) => {
        const pct = Math.max(4, Math.round((item.value / maxVal) * 100));
        return (
          <View key={item.label} style={barStyles.row}>
            <Text style={barStyles.label} numberOfLines={1}>
              {item.label}
            </Text>
            <View style={barStyles.track}>
              <View style={[barStyles.fill, { width: `${pct}%` }]} />
            </View>
            <Text style={barStyles.value}>{item.value}%</Text>
          </View>
        );
      })}
    </View>
  );
}

/**
 * @param {{ data: { label: string, value: number }[] }} props
 */
export function WeekdayBarChart({ data }) {
  const maxVal = Math.max(1, ...data.map((d) => d.value));

  return (
    <View style={weekStyles.wrap}>
      {data.map((item) => {
        const h = Math.max(8, Math.round((item.value / maxVal) * 72));
        return (
          <View key={item.label} style={weekStyles.col}>
            <View style={[weekStyles.bar, { height: h }]} />
            <Text style={weekStyles.day}>{item.label}</Text>
          </View>
        );
      })}
    </View>
  );
}

/**
 * @param {{ points: { score: number }[] }} props — score 0–1 per day
 */
export function TrendSparkline({ points }) {
  const maxH = 56;
  return (
    <View style={sparkStyles.wrap}>
      {points.map((pt, i) => {
        const h = Math.max(4, Math.round(pt.score * maxH));
        const active = pt.score >= 0.75;
        return (
          <View key={i} style={sparkStyles.col}>
            <View
              style={[
                sparkStyles.bar,
                { height: h },
                active ? sparkStyles.barOn : pt.score > 0 ? sparkStyles.barMid : sparkStyles.barOff,
              ]}
            />
          </View>
        );
      })}
    </View>
  );
}

/** @param {{ value: number, max: number, color?: string }} props */
export function MiniRingStat({ value, max, color = CF.cyan }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <View style={miniRingStyles.wrap}>
      <DonutGauge percent={pct} size={48} stroke={5}>
        <Text style={miniRingStyles.pct}>{Math.round(pct)}%</Text>
      </DonutGauge>
      <View style={[miniRingStyles.dot, { backgroundColor: color }]} />
    </View>
  );
}

export { DASH };

const barStyles = StyleSheet.create({
  wrap: { gap: 10, paddingVertical: 4 },
  row: { flexDirection: "row", alignItems: "center", gap: 8 },
  label: { width: 72, fontSize: 11, fontWeight: "700", color: DASH.textMuted, ...DASH.textShadowSoft },
  track: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    backgroundColor: "rgba(45, 212, 191, 0.22)",
    overflow: "hidden",
  },
  fill: {
    height: "100%",
    borderRadius: 4,
    backgroundColor: CF.primaryBright,
  },
  value: { width: 32, fontSize: 11, fontWeight: "800", color: DASH.text, textAlign: "right", ...DASH.textShadow },
});

const weekStyles = StyleSheet.create({
  wrap: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    height: 96,
    paddingTop: 8,
  },
  col: { flex: 1, alignItems: "center", gap: 6 },
  bar: {
    width: "70%",
    maxWidth: 28,
    borderRadius: 6,
    backgroundColor: CF.primary,
    minHeight: 8,
  },
  day: { fontSize: 9, fontWeight: "700", color: DASH.textSoft, ...DASH.textShadowSoft },
});

const sparkStyles = StyleSheet.create({
  wrap: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    height: 64,
    gap: 3,
    paddingHorizontal: 2,
  },
  col: { flex: 1, alignItems: "center", justifyContent: "flex-end" },
  bar: { width: "100%", borderRadius: 4, maxWidth: 14 },
  barOn: { backgroundColor: CF.primaryBright },
  barMid: { backgroundColor: CF.cyan },
  barOff: { backgroundColor: "rgba(148, 163, 184, 0.35)" },
});

const miniRingStyles = StyleSheet.create({
  wrap: { alignItems: "center", justifyContent: "center" },
  pct: { fontSize: 11, fontWeight: "800", color: DASH.text, ...DASH.textShadow },
  dot: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});
